import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import {
  InputLabel,
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import dayjs from "dayjs";
import {
  Button,
  Card,
  CardContent,
  InputDate,
  InputNote,
} from "@sellar-io/components";
import { useSupplierStorefrontAvailability } from "../../../../hooks/api/v2/storefrontAvailability/useSupplierStorefrontAvailability";
import { useSupplierStorefrontAvailabilityUpdate } from "../../../../hooks/api/v2/storefrontAvailability/useSupplierStorefrontAvailabilityUpdate";
import {
  StorefrontAvailabilityGetResponseType,
  StorefrontAvailabilityNotificationListResponseType,
  StorefrontAvailabilityUpdateRequestType,
  StorefrontAvailabilityUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { AvailabilityNotificationsList } from "./AvailabilityNotificationsList";
import { useSupplierStorefrontAvailabilityNotificationList } from "../../../../hooks/api/v2/storefrontAvailability/notifications/useSupplierStorefrontAvailabilityNotificationList";

const dateAsStringInputFormat = (date: Date | null) => {
  if (!date) {
    return null;
  }
  return dayjs(date).format("YYYY-MM-DD");
};

// The availability-check notification cron only runs hourly, so a minute-level
// cutoff would imply a precision the system can't actually honour. Times are
// restricted to whole hours via an explicit hour dropdown (rather than a
// native time input, whose minute-hiding `step` behaviour isn't reliable
// across browsers) both on input and when deriving a default from existing
// data.
const HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, hour) => hour.toString().padStart(2, "0") + ":00",
);

const DEFAULT_START_TIME = "09:00";
const DEFAULT_END_TIME = "16:00";

const timeAsStringInputFormat = (
  date: Date | null,
  fallback: string,
): string => {
  if (!date) {
    return fallback;
  }
  return dayjs(date).format("HH:00");
};

const combineDateAndTime = (date: string, time: string) =>
  dayjs(`${date} ${time}`, "YYYY-MM-DD HH:mm").toDate();

type StorefrontAvailabilityFormValues = {
  type: "continuous" | "scheduled";
  startDate: string | null;
  startTime: string;
  endDate: string | null;
  endTime: string;
};

const getFormValues = (
  existingData: StorefrontAvailabilityGetResponseType,
): StorefrontAvailabilityFormValues => ({
  type: existingData.type,
  startDate: dateAsStringInputFormat(existingData.startDate),
  startTime: timeAsStringInputFormat(
    existingData.startDate,
    DEFAULT_START_TIME,
  ),
  endDate: dateAsStringInputFormat(existingData.endDate),
  endTime: timeAsStringInputFormat(existingData.endDate, DEFAULT_END_TIME),
});

const validateStartDate = (values: StorefrontAvailabilityFormValues) => {
  if (!values.startDate) return "A start date must be provided";
  if (
    values.endDate &&
    dayjs(combineDateAndTime(values.startDate, values.startTime)).isAfter(
      combineDateAndTime(values.endDate, values.endTime),
    )
  ) {
    return "Start date and time must be before end date and time";
  }
  return undefined;
};

const validateEndDate = (values: StorefrontAvailabilityFormValues) => {
  if (!values.endDate) return "An end date must be provided";
  if (
    values.startDate &&
    dayjs(combineDateAndTime(values.startDate, values.startTime)).isAfter(
      combineDateAndTime(values.endDate, values.endTime),
    )
  ) {
    return "End date and time must be after start date and time";
  }
  return undefined;
};

const validateForm = ({
  value,
}: {
  value: StorefrontAvailabilityFormValues;
}) => {
  if (value.type === "continuous") return undefined;

  const startDateError = validateStartDate(value);
  const endDateError = validateEndDate(value);

  if (!startDateError && !endDateError) return undefined;

  return {
    fields: {
      startDate: startDateError,
      endDate: endDateError,
    },
  };
};

const StorefrontAvailabilityForm = ({
  supplierId,
  existingData,
  notificationList,
  onSubmit,
  isFormSubmitting,
  errorMessage,
}: {
  supplierId: number;
  existingData: StorefrontAvailabilityGetResponseType;
  notificationList: StorefrontAvailabilityNotificationListResponseType;
  onSubmit: (
    data: StorefrontAvailabilityUpdateRequestType,
  ) => Promise<StorefrontAvailabilityUpdateResponseType>;
  isFormSubmitting: boolean;
  errorMessage: string;
}) => {
  const form = useForm({
    defaultValues: getFormValues(existingData),
    validators: {
      onChange: validateForm,
      onSubmit: validateForm,
    },
    onSubmit: async ({ value }) => {
      try {
        await onSubmit({
          type: value.type,
          startDate:
            value.type === "scheduled" && value.startDate
              ? combineDateAndTime(value.startDate, value.startTime)
              : null,
          endDate:
            value.type === "scheduled" && value.endDate
              ? combineDateAndTime(value.endDate, value.endTime)
              : null,
        });
        form.reset(value);
      } catch {
        // Mutation error is surfaced separately via `errorMessage`; leave
        // the form dirty so the user doesn't lose their unsaved changes.
      }
    },
  });

  const { type, startDate, isDirty } = useStore(form.store, (state) => ({
    type: state.values.type,
    startDate: state.values.startDate,
    isDirty: state.isDirty,
  }));

  useEffect(() => {
    const newValues = getFormValues(existingData);
    const currentValues = form.state.values;

    form.reset({
      type: newValues.type,
      startDate: newValues.startDate ?? currentValues.startDate,
      startTime: newValues.startDate
        ? newValues.startTime
        : currentValues.startTime,
      endDate: newValues.endDate ?? currentValues.endDate,
      endTime: newValues.endDate ? newValues.endTime : currentValues.endTime,
    });
  }, [existingData, form]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
      className={"flex flex-col"}
    >
      <CardContent className={"flex flex-col gap-4 p-4"}>
        {errorMessage.length > 0 && (
          <Alert className="w-full" type="error" message={errorMessage} />
        )}

        <div className="flex flex-col justify-between">
          <div>
            <InputLabel>Availability type</InputLabel>
            <form.Field name="type">
              {(field) => (
                <InputContainer>
                  <InputSelect
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => {
                      const value = event.target.value as
                        | "continuous"
                        | "scheduled";
                      field.handleChange(value);
                    }}
                    required
                  >
                    <InputOption value={"continuous"}>Continuous</InputOption>
                    <InputOption value={"scheduled"}>Scheduled</InputOption>
                  </InputSelect>
                </InputContainer>
              )}
            </form.Field>
          </div>

          {type === "scheduled" && (
            <div className={"my-4 flex flex-col gap-2"}>
              {/* start date, end date, type */}

              <div>
                <InputLabel>Availability start date</InputLabel>
                <form.Field name="startDate">
                  {(dateField) => (
                    <form.Field name="startTime">
                      {(timeField) => (
                        <>
                          <div className="flex gap-2">
                            <InputContainer className="flex-1">
                              <InputDate
                                className={"w-full"}
                                name={dateField.name}
                                value={dateField.state.value ?? ""}
                                onBlur={dateField.handleBlur}
                                onChange={(event) =>
                                  dateField.handleChange(
                                    event.target.value || null,
                                  )
                                }
                                required
                              />
                            </InputContainer>
                            <InputContainer className="w-32">
                              <InputSelect
                                className={"w-full"}
                                name={timeField.name}
                                value={timeField.state.value}
                                onBlur={timeField.handleBlur}
                                onChange={(event) =>
                                  timeField.handleChange(event.target.value)
                                }
                                required
                              >
                                {HOUR_OPTIONS.map((hourOption) => (
                                  <InputOption
                                    key={hourOption}
                                    value={hourOption}
                                  >
                                    {hourOption}
                                  </InputOption>
                                ))}
                              </InputSelect>
                            </InputContainer>
                          </div>
                          {dateField.state.meta.errors[0] && (
                            <InputNote className="text-red-600">
                              {dateField.state.meta.errors[0]}
                            </InputNote>
                          )}
                        </>
                      )}
                    </form.Field>
                  )}
                </form.Field>
                <InputNote>
                  Products will become accessible at the date and time specified
                </InputNote>

                <div className="my-2">
                  <AvailabilityNotificationsList
                    availabilityNotificationList={notificationList.filter(
                      (notification) =>
                        notification.intervalMarkerType === "start",
                    )}
                    intervalMarker={"start"}
                    supplierId={supplierId}
                  />
                </div>
              </div>
              <div>
                <InputLabel>Availability end date</InputLabel>
                <form.Field name="endDate">
                  {(dateField) => (
                    <form.Field name="endTime">
                      {(timeField) => (
                        <>
                          <div className="flex gap-2">
                            <InputContainer className="flex-1">
                              <InputDate
                                className={"w-full"}
                                name={dateField.name}
                                value={dateField.state.value ?? ""}
                                onBlur={dateField.handleBlur}
                                onChange={(event) =>
                                  dateField.handleChange(
                                    event.target.value || null,
                                  )
                                }
                                min={dayjs(startDate)
                                  .startOf("day")
                                  .format("YYYY-MM-DD")}
                                required
                              />
                            </InputContainer>
                            <InputContainer className="w-32">
                              <InputSelect
                                className={"w-full"}
                                name={timeField.name}
                                value={timeField.state.value}
                                onBlur={timeField.handleBlur}
                                onChange={(event) =>
                                  timeField.handleChange(event.target.value)
                                }
                                required
                              >
                                {HOUR_OPTIONS.map((hourOption) => (
                                  <InputOption
                                    key={hourOption}
                                    value={hourOption}
                                  >
                                    {hourOption}
                                  </InputOption>
                                ))}
                              </InputSelect>
                            </InputContainer>
                          </div>
                          {dateField.state.meta.errors[0] && (
                            <InputNote className="text-red-600">
                              {dateField.state.meta.errors[0]}
                            </InputNote>
                          )}
                        </>
                      )}
                    </form.Field>
                  )}
                </form.Field>
                <InputNote>
                  Products will no longer be accessible after the date and time
                  specified
                </InputNote>

                <div className="my-2">
                  <AvailabilityNotificationsList
                    availabilityNotificationList={notificationList.filter(
                      (notification) =>
                        notification.intervalMarkerType === "end",
                    )}
                    intervalMarker={"end"}
                    supplierId={supplierId}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            className="flex justify-end"
            type={"submit"}
            loading={isFormSubmitting}
            disabled={!isDirty || isFormSubmitting}
          >
            Save changes
          </Button>
        </div>
      </CardContent>
    </form>
  );
};

export const StorefrontAvailabilityCard = ({
  supplierId,
}: {
  supplierId: number;
}) => {
  const storefrontAvailabilityQuery =
    useSupplierStorefrontAvailability(supplierId);

  const storefrontAvailabilityUpdateMutation =
    useSupplierStorefrontAvailabilityUpdate(supplierId);

  const storefrontAvailabilityList =
    useSupplierStorefrontAvailabilityNotificationList(supplierId);

  const storefrontAvailabilityUpdateMutationErrorMessage =
    storefrontAvailabilityUpdateMutation.error
      ? (apiV2AssertError(storefrontAvailabilityUpdateMutation.error)?.response
          ?.data.message ?? "Unknown error")
      : "";

  if (storefrontAvailabilityQuery.isError) {
    return <div>Error getting storefront availability</div>;
  }

  if (!storefrontAvailabilityQuery.data) {
    return <SpinnerLegacy />;
  }

  return (
    <Card className="bg-white">
      <StorefrontAvailabilityForm
        supplierId={supplierId}
        existingData={storefrontAvailabilityQuery.data}
        notificationList={storefrontAvailabilityList.data ?? []}
        isFormSubmitting={storefrontAvailabilityUpdateMutation.isPending}
        onSubmit={(data) => {
          return storefrontAvailabilityUpdateMutation.mutateAsync(data);
        }}
        errorMessage={storefrontAvailabilityUpdateMutationErrorMessage}
      />
    </Card>
  );
};
